'use client';

/**
 * ClaimLedgerView — traceable line-item G/L for a single claim.
 * Fetches GET /api/claims/{id}/ledger and renders the full lifecycle
 * (charge → 837 submit → 277 accept → denial → appeal → 835 payment) with
 * debit/credit and a running A/R balance for biller review. Self-contained;
 * usable in both the legacy and v2 member records and the claims page.
 */

import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { claimsApi, ClaimLedger } from '@/services/api/claims';
import { formatLocalDate } from '@/utils/dateTime';

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const EVENT_LABEL: Record<string, string> = {
  charge: 'Charge billed',
  status_change: 'Status',
  edi_837: 'Clearinghouse submit',
  edi_277: 'Clearinghouse ack',
  edi_835: 'Remittance received',
  denial: 'Denial',
  appeal: 'Appeal',
  payment: 'Payment posted',
};

function eventColor(t: string): string {
  if (t === 'payment' || t === 'appeal') return 'text-green-700 dark:text-green-400';
  if (t === 'denial') return 'text-red-600 dark:text-red-400';
  if (t.startsWith('edi')) return 'text-blue-600 dark:text-blue-400';
  return 'text-gray-600 dark:text-gray-300';
}

export default function ClaimLedgerView({ claimId }: { claimId: number }) {
  const [ledger, setLedger] = useState<ClaimLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    claimsApi
      .getClaimLedger(claimId)
      .then((d) => {
        if (active) setLedger(d);
      })
      .catch(() => {
        if (active) setError('Unable to load claim ledger');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [claimId]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
      </div>
    );
  if (error || !ledger)
    return <div className="py-4 text-sm text-gray-500 dark:text-gray-400">{error || 'No ledger'}</div>;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
        <span>
          Ledger · {ledger.claim_number} · {ledger.member_name}
        </span>
        <span>
          Outstanding A/R: <span className="font-semibold tabular-nums">{usd(ledger.outstanding_ar)}</span>
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Reference</th>
              <th className="px-3 py-2 text-right">Debit</th>
              <th className="px-3 py-2 text-right">Credit</th>
              <th className="px-3 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {ledger.events.map((e, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                  {e.event_date ? formatLocalDate(e.event_date) : '—'}
                </td>
                <td className={`px-3 py-1.5 ${eventColor(e.event_type)}`}>
                  <span className="font-medium">{EVENT_LABEL[e.event_type] || e.event_type}</span>
                  {e.line_number != null && <span className="ml-1 text-xs text-gray-400">(line {e.line_number})</span>}
                  <div className="text-xs text-gray-500 dark:text-gray-400">{e.description}</div>
                </td>
                <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{e.reference_number || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{e.debit ? usd(e.debit) : ''}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{e.credit ? usd(e.credit) : ''}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{usd(e.running_balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
